import React from 'react';
import styled from 'styled-components';

import { Container } from '@nfs-react/components';

import { useImg, useText } from 'hooks';
import { MOBILE, TABLET } from 'helpers/styles/media';
import { ChangeableBackgroundSection } from 'helpers/components';

const Comments = () => {
  const comments = [
    {
      id: '1',
      photo: useImg('comment_img_1.webp', 'comment_img_1'),
      name: useText('comment__name_1', true),
      text: useText('comments__text_1', true),
      like: useText('comments__like', true),
      time: useText('comments__time_1', true),
      children: [
        {
          id: '1_1',
          photo: useImg('comment_img_1_1.webp', 'comment_img_1_1'),
          name: useText('comment__name_1_1', true),
          text: useText('comments__text_1_1', true),
          like: useText('comments__like', true),
          time: useText('comments__time_1_1', true),
        },
      ],
    },
    {
      id: '2',
      photo: useImg('comment_img_2.webp', 'comment_img_2'),
      name: useText('comment__name_2', true),
      text: useText('comments__text_2', true),
      like: useText('comments__like', true),
      time: useText('comments__time_2', true),
    },
    {
      id: '3',
      photo: useImg('comment_img_3.webp', 'comment_img_3'),
      name: useText('comment__name_3', true),
      text: useText('comments__text_3', true),
      like: useText('comments__like', true),
      time: useText('comments__time_3', true),
    },
    {
      id: '4',
      photo: useImg('comment_img_4.webp', 'comment_img_4'),
      name: useText('comment__name_4', true),
      text: useText('comments__text_4', true),
      like: useText('comments__like', true),
      time: useText('comments__time_4', true),
      showCommentsText: useText('comments__show_text', true),
      children: [
        {
          id: '4_1',
          photo: useImg('comment_img_4_1.webp', 'comment_img_4_1'),
          name: useText('comment__name_4_1', true),
          text: useText('comments__text_4_1', true),
          like: useText('comments__like', true),
          time: useText('comments__time_4_1', true),
        },
        {
          id: '4_2',
          photo: useImg('comment_img_4_2.webp', 'comment_img_4_2'),
          name: useText('comment__name_4_2', true),
          text: useText('comments__text_4_2', true),
          like: useText('comments__like', true),
          time: useText('comments__time_4_2', true),
        },
      ],
    },
    {
      id: '5',
      photo: useImg('comment_img_5.webp', 'comment_img_5'),
      name: useText('comment__name_5', true),
      text: useText('comments__text_5', true),
      like: useText('comments__like', true),
      time: useText('comments__time_5', true),
    },
  ];
  return (
    <ChangeableBackgroundSection
      id="comments-wrapper-bg"
      defaultStyle={{ background: '#fff' }}
    >
      <ArticleContainer>
        <Wrapper>
          <Title>{useText('comments__title', true)}</Title>
          <Text>{useText('comments__text', true)}</Text>
          <BlockSortBy>
            {useText('comments__sort', true)}
            {useText('comments__sort_link', true)}
          </BlockSortBy>
          <WrapPost>
            {useImg('post_img.webp', 'post_img')}
            <Post>
              <textarea placeholder="Add a comment..." />
              <input type='checkbox' />
              <button type="button" disabled>
                {useText('comments__post_btn', true)}
              </button>
            </Post>
          </WrapPost>
          {comments.map(({ id, photo, name, text, like, time, children, showCommentsText }) => {
            return (
              <WrapComments key={id}>
                <PhotoLink>{photo}</PhotoLink>
                <Comment>
                  <CommentName>{name}</CommentName>
                  <CommentText>{text}</CommentText>
                  <CommentLink>
                    <CommentLike>{like}</CommentLike>
                    <CommentTime>{time}</CommentTime>
                  </CommentLink>
                  {children && (
                    children.map(({
                      id: childId,
                      photo: childPhoto,
                      name: childName,
                      text: childText,
                      like: childLike,
                      time: childTime,
                    }) => (
                      <WrapCommentsChild key={childId}>
                        <PhotoLinkChild>{childPhoto}</PhotoLinkChild>
                        <Comment>
                          <CommentName>{childName}</CommentName>
                          <CommentText>{childText}</CommentText>
                          <CommentLink>
                            <CommentLike>{childLike}</CommentLike>
                            <CommentTime>{childTime}</CommentTime>
                          </CommentLink>
                        </Comment>
                      </WrapCommentsChild>
                    ))
                  )}
                  {showCommentsText && (
                    <ShowTextLink>{showCommentsText}</ShowTextLink>
                  )}
                </Comment>
              </WrapComments>
            );
          })}
          <CommentPlugin>{useText('comments__fb_plugin', true)}</CommentPlugin>
          <Button>{useText('comments__btn', true)}</Button>
        </Wrapper>
      </ArticleContainer>
    </ChangeableBackgroundSection>
  );
};

const ArticleContainer = styled(Container)`
  display: flex;
  flex-direction: column;

  @media ${TABLET} {
    max-width: 1200px;
  }

  @media ${MOBILE} {
    flex-direction: column;
    max-width: 768px;
    padding: 0;
  }
`;

const Wrapper = styled.div`
  max-width: 800px;

  @media ${TABLET} {
    padding-right: 10px;
    padding-left: 20px;
    max-width: 700px;
  }

  @media ${MOBILE} {
    padding: 0 20px;
  }
`;

const Text = styled.div`
  letter-spacing: 0.5px;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #1d1d1d;
  padding-bottom: 10px;

  a {
    color: #e60505;
    font-weight: bold;
    cursor: pointer;
    text-decoration: none;
    border-bottom: 1px dashed #e60505;
  }
`;

const Title = styled.div`
  h2 {
    margin: 0;
    color: #141414;
    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
    margin-block: 24px;
  }
`;

const BlockSortBy = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #e9ebee;
  margin-bottom: 24px;
  padding: 8px 0;

  div:first-child {
    font-size: 14px;
    line-height: 25px;
    font-weight: 700;
  }

  a {
    background-color: #f6f7f9;
    border: 1px solid #ced0d4;
    border-radius: 2px;
    font-size: 12px;
    font-weight: 700;
    padding: 5px 8px;
    width: 50px;
    display: inline-block;
  }
`;

const WrapPost = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const Post = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  background: #f6f7f9;
  gap: 8px;

  textarea {
    width: 100%;
    padding: 12px 8px;
  }

  button {
    background-color: #9cb4d8;
    border-color: #9cb4d8;
    font-size: 14px;
    line-height: 22px;
    color: #fff;
    border-radius: 2px;
    border: 1px solid;
    margin-bottom: 8px;
  }
`;

const WrapComments = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const WrapCommentsChild = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  border-left: 1px dotted #d3d6db;
  padding-left: 10px;

  img {
    width: 36px;
  }
`;

const ShowTextLink = styled.div`
  background: #f6f7f9;
  padding: 8px;
  margin: 12px 0 12px 8px;

  a {
    color: #365899;
    cursor: pointer;
    text-decoration: none;
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;

    :hover {
      text-decoration: underline;
    }
  }
`;

const PhotoLink = styled.a`
  cursor: pointer;
  min-width: max-content;

  img {
    width: 50px;
  }
`;

const PhotoLinkChild = styled.a`
  cursor: pointer;
  min-width: max-content;

  img {
    width: 36px;
  }
`;

const Comment = styled.div`
  display: flex;
  flex-direction: column;
`;

const CommentName = styled.div`
  a {
    color: #365899;
    cursor: pointer;
    font-weight: 700;
    font-size: 14px;
    line-height: 19px;
    text-decoration: none;

    :hover {
      text-decoration: underline;
    }
  }
`;

const CommentText = styled.div`
  color: #141414;
  font-size: 14px;
  line-height: 19px;
  font-weight: 400;
  margin: 4px 0;

  a {
    color: #4267b2;
    text-decoration: none;

    :hover {
      text-decoration: underline;
    }
  }
`;

const CommentLink = styled.div`
  padding-top: 4px;
  display: flex;
  gap: 20px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
`;

const CommentLike = styled.div`
  color: #90949c;

  a {
    color: #4267b2;
    cursor: pointer;
    text-decoration: none;

    :hover {
      text-decoration: underline;
    }
  }
`;

const CommentTime = styled.div`
  color: #90949c;

  a {
    color: #90949c;
    cursor: pointer;
    text-decoration: none;

    :hover {
      text-decoration: underline;
    }
  }
`;

const CommentPlugin = styled.div`
  margin-top: 12px;
  border-top: 1px solid #e9ebee;
  padding: 10px 0;

  a {
    color: #4267b2;
    cursor: pointer;
    text-decoration: none;
    font-size: 11px;
    line-height: 15px;
    padding-left: 20px;

    :hover {
      text-decoration: underline;
    }
  }
`;

const Button = styled.div`
  margin: 20px auto 40px;
  display: block;
  background-color: #e90e0e;
  font-size: 21px;
  line-height: 21px;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
  color: #fff;
  padding: 12px;
  border-radius: 8px;
  border: none;
  width: 70%;
  cursor: pointer;

  a {
    text-decoration: none;
    color: #fff;
  }

  &:hover {
    transform: scale(1.1);
  }
`;

export default Comments;
